// Apolo IDE — GlassBoxDrawer (E2, constitution-upgrade). Transparency by design:
// show the user EXACTLY what was injected into the model this turn — the assembled
// system prompt, per block (redacted + capped server-side) — plus the v2 Evaluator's
// craft verdicts. The Craft tab is READ-ONLY: it never approves anything, respecting
// the evaluator LAW (only a human PATCH on the review queue moves an item). No Cache
// tab: the CACHE_BOUNDARY isn't wired into production prompts yet, so a static/dynamic
// split would be misleading — added when the boundary lands.

import { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../../lib/api.js';
import { fetchCraftVerdicts } from '../../../hooks/useCraftReview.js';

const TABS = [
  { key: 'prompt', label: 'Prompt' },
  { key: 'craft', label: 'Craft' },
];

const scoreTone = (s) => (s >= 7 ? 'text-apolo-success' : s >= 4 ? 'text-apolo-warning' : 'text-apolo-error');

function PromptTab({ sessionId }) {
  const [state, setState] = useState({ loading: true, events: [], error: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, events: [], error: null });
    apiFetch(`/api/code-ide/sessions/${encodeURIComponent(sessionId)}/prompt-blocks`)
      .then((r) => {
        if (!alive) return;
        if (r.ok) setState({ loading: false, events: r.data?.events || [], error: null });
        else setState({ loading: false, events: [], error: r.error || `HTTP ${r.status}` });
      })
      .catch((e) => { if (alive) setState({ loading: false, events: [], error: e?.message || 'falha' }); });
    return () => { alive = false; };
  }, [sessionId]);

  if (state.loading) return <p className="px-4 py-6 text-[12px] text-apolo-textTertiary">Carregando o prompt montado…</p>;
  if (state.error) return <p className="px-4 py-6 text-[12px] text-apolo-error">Erro: {state.error}</p>;
  const latest = state.events[0];
  if (!latest || !latest.segments?.length) {
    return <p className="px-4 py-6 text-[12px] text-apolo-textTertiary">Nenhum prompt capturado ainda nesta sessão. Rode um turno com ferramentas e reabra.</p>;
  }
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <div className="flex items-center gap-2 px-1 text-[11px] text-apolo-textTertiary">
        {/* WS-F/F8 (incidente tastedesign): este número conta SÓ os segmentos dinâmicos
            do system prompt — não a constituição, o corpo de skills /slash, nem o
            histórico. Rotular o escopo evita lê-lo como "o tamanho do prompt". */}
        <span title="Só os segmentos dinâmicos do system prompt (regras, steering, memória, RAG…). NÃO inclui a constituição, o corpo de skills /slash, nem o histórico de mensagens — a entrada real enviada ao modelo é maior (ver tokens_in no fim do turno).">
          ~{latest.tokens} tok · segmentos dinâmicos
        </span>
        {latest.truncated && <span className="rounded-apolo-md bg-apolo-surface2 px-1.5 py-0.5">truncado</span>}
        <span className="ml-auto">{state.events.length} snapshot(s)</span>
      </div>
      {latest.segments.map((seg, i) => (
        <details key={`${seg.label}-${i}`} open={i === 0} className="overflow-hidden rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1">
          <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[12px] font-semibold text-apolo-textPrimary outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent">
            <span className="text-apolo-accent">{seg.label}</span>
            <span className="ml-auto text-[10px] font-normal text-apolo-textTertiary">~{seg.tokens} tok{seg.truncated ? ' · truncado' : ''}</span>
          </summary>
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words border-t border-apolo-borderSubtle px-3 py-2 text-[11.5px] leading-relaxed text-apolo-textSecondary">{seg.text}</pre>
        </details>
      ))}
    </div>
  );
}

function CraftTab({ sessionId }) {
  const [state, setState] = useState({ loading: true, verdicts: [], error: null });
  useEffect(() => {
    let alive = true;
    setState({ loading: true, verdicts: [], error: null });
    fetchCraftVerdicts(sessionId)
      .then((r) => {
        if (!alive) return;
        if (r.ok) setState({ loading: false, verdicts: r.data?.verdicts || [], error: null });
        else setState({ loading: false, verdicts: [], error: r.error || `HTTP ${r.status}` });
      })
      .catch((e) => { if (alive) setState({ loading: false, verdicts: [], error: e?.message || 'falha' }); });
    return () => { alive = false; };
  }, [sessionId]);

  if (state.loading) return <p className="px-4 py-6 text-[12px] text-apolo-textTertiary">Carregando os vereditos de craft…</p>;
  if (state.error) return <p className="px-4 py-6 text-[12px] text-apolo-error">Erro: {state.error}</p>;
  if (!state.verdicts.length) {
    return <p className="px-4 py-6 text-[12px] text-apolo-textTertiary">Sem vereditos de craft pra esta sessão — o Discovery roda DEPOIS do green. Read-only: esta aba nunca aprova nada (LEI v2).</p>;
  }
  return (
    <div className="flex flex-col gap-2 px-3 py-3">
      <p className="px-1 text-[10.5px] text-apolo-textTertiary">Opinião de craft do Evaluator — read-only; nunca aprova merge (LEI v2).</p>
      {state.verdicts.map((v, i) => (
        <div key={`${v.criterion}-${i}`} className="rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface1 px-3 py-2">
          <div className="flex items-center gap-2 text-[12px]">
            <span className="font-semibold text-apolo-textPrimary">{v.criterion}</span>
            <span className={`ml-auto font-mono text-[12px] ${scoreTone(Number(v.score))}`}>{v.score}/10</span>
          </div>
          {v.evidence && <p className="mt-1 text-[11.5px] text-apolo-textSecondary">{v.evidence}</p>}
          {v.fixSuggestion && <p className="mt-1 text-[11px] text-apolo-textTertiary"><span className="text-apolo-accent">fix:</span> {v.fixSuggestion}</p>}
        </div>
      ))}
    </div>
  );
}

export default function GlassBoxDrawer({ sessionId, open, onClose }) {
  const [tab, setTab] = useState('prompt');
  const closeRef = useRef(null);
  const panelRef = useRef(null);

  // a11y — ESC closes (preventDefault so it doesn't bubble to a browser default),
  // and a focus trap keeps Tab inside the dialog (so background content stays
  // unreachable without needing `inert` on siblings rendered elsewhere in the tree).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose?.(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = panel.querySelectorAll(
        'button, [href], input, select, textarea, summary, [tabindex]:not([tabindex="-1"])',
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    const t = setTimeout(() => closeRef.current?.focus(), 0);
    return () => { document.removeEventListener('keydown', onKey, true); clearTimeout(t); };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Glass Box — prompt e craft desta sessão">
      <button type="button" aria-label="Fechar Glass Box" onClick={onClose} className="absolute inset-0 cursor-default bg-apolo-overlay" />
      <div ref={panelRef} className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-apolo-border bg-apolo-bg shadow-2xl">
        <header className="flex items-center gap-2 border-b border-apolo-border px-3 py-2">
          <span className="text-[13px] font-semibold text-apolo-textPrimary">Glass Box</span>
          <span className="text-[10.5px] text-apolo-textTertiary">o que o modelo recebeu</span>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="ml-auto rounded-apolo-md px-2 py-1 text-[13px] text-apolo-textTertiary outline-none hover:bg-apolo-surface2 focus-visible:ring-2 focus-visible:ring-apolo-accent"
          >
            ✕
          </button>
        </header>
        <nav className="flex gap-1 border-b border-apolo-border px-2 py-1.5" aria-label="Abas do Glass Box">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'true' : undefined}
              className={`rounded-apolo-md px-3 py-1 text-[12px] outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent ${tab === t.key ? 'bg-apolo-surface2 font-semibold text-apolo-textPrimary' : 'text-apolo-textTertiary hover:bg-apolo-surface1'}`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="min-h-0 flex-1 overflow-auto">
          {tab === 'prompt' ? <PromptTab sessionId={sessionId} /> : <CraftTab sessionId={sessionId} />}
        </div>
      </div>
    </div>
  );
}
